import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { IExamMasterCodeItem } from '../api/user/model';
import { KeyValueResponse } from '../api';
import { JobNameModel } from '../models/JobName';

export interface DropDownItem {
  children: string;
  key: string;
  value: string;
}

export interface APIDropdownReturn {
  /**
   * Lookup data for dropdown to render
   */
  functionCode: any;
  /**
   * Will always be the first value in the data list
   */
  functionDefaultValue: any;
  /**
   * Current selected value
   */
  selectType: string;
  /**
   * Function to change current selected value
   */
  setSelectType: Dispatch<SetStateAction<string>>;
  /**
   * need to map description for display
   */
  typeMapping: { [key: string]: any };
}

/**
 * A hook to help retrieve lookup data to be display in a dropdown
 * @param useQuery Query to call api
 * @param emptyFirst Indicate the dropdown list should have a default empty value at first item
 * @param mapFunc Optional mapping function to change api data structure to fit
 * @returns
 */
export const useAPIDropdown = (
  useQuery: Function,
  emptyFirst = false,
  mapFunc?: Function
): APIDropdownReturn => {
  const { data } = useQuery();

  const functionCode = useMemo<DropDownItem[]>(() => {
    if (!data) {
      return [];
    }
    if (mapFunc) {
      return data.map(mapFunc);
    }
    const list: DropDownItem[] = [];
    if (emptyFirst) {
      list.push({
        children: 'Select ..',
        key: '-',
        value: '-',
      });
    }

    data.forEach((item: string) =>
      list.push({
        children: item,
        key: item,
        value: item,
      })
    );

    return list;
  }, [data, emptyFirst, mapFunc]);

  const functionDefaultValue = useMemo(
    () => (functionCode.length > 0 ? functionCode[0].value : ''),
    [functionCode]
  );

  const [selectType, setSelectType] = useState(functionDefaultValue);

  const typeMapping = useMemo(() => {
    const mapping: { [key: string]: any } = {};
    functionCode.forEach((element: DropDownItem) => {
      mapping[element.value] = element;
    });
    return mapping;
  }, [functionCode]);

  useEffect(() => {
    setSelectType(functionDefaultValue);
  }, [functionDefaultValue]);

  return {
    functionCode,
    typeMapping,
    functionDefaultValue,
    selectType,
    setSelectType,
  };
};

export const SelectMappingtoSelect = (item: DropDownItem) => ({
  children: item.children,
  key: item.key,
  value: item.value,
});

export const iExamMasterCodeMappingToSelect = (item: IExamMasterCodeItem) => ({
  children: item.codeValueDescription,
  key: item.codeId,
  value: item.codeValue,
});

export const dropDownMappingToSelect = (item: KeyValueResponse) => ({
  children: item.description,
  key: item.code,
  value: item.code,
});

export const dropDownMappingDescriptionAsValue = (item: KeyValueResponse) => ({
  children: item.description,
  key: item.code,
  value: item.description,
});

export const dropDownMappingPureArray = (item: string) => ({
  children: item,
  key: item,
  value: item,
});

export const jobNameMappingToSelect = (item: JobNameModel) => ({
  children: item.displayName,
  key: item.uuid,
  value: item.uuid,
});